import {useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode} from 'react';
import type {MoneyTone} from '@pokpok/astryx';
import {errorMessageKey} from '../../api/errors';
import type {UnavailabilityReason} from '../../api/types';
import {ROUTES} from '../../app/routes';
import {useRouter} from '../../app/router';
import {lineTotalSatang, useCart, type CartLine} from '../../cart';
import {useI18n} from '../../i18n';
import type {MessageKey} from '../../i18n';
import {useMenu} from '../../menu';
import {orderTotalTone, useOrder} from '../../order';
import {useGuestSession} from '../../session';
import {
  ActionLink,
  BackLink,
  Button,
  Card,
  Money,
  Screen,
  ScreenBody,
  ScreenFooter,
  SplitRow,
  Stack,
  StatusPill,
  TerminalNotice,
  TopBar,
} from '../../ui';
import {LanguageToggle} from '../Q02LanguageToggle';

/**
 * # Q7 · Order-first submit — the full-service default
 *
 * Route `/review`. The kitchen fires now; the guest pays at the counter later.
 *
 * ## The one thing this screen is for
 *
 * **The guest incurs a debt without paying.** Everything else on the screen
 * serves that: the summary is what they are committing to, `q7.payLaterNote`
 * says out loud that money happens later, and the sent panel
 * (`q7.sentTitle` + `q7.sentBody`) is unambiguous that food is coming and the
 * bill is at the counter. A guest who is unsure whether the order went in waves
 * at a server, and the labour saving this whole surface exists to produce is
 * gone.
 *
 * ## Two moments, one route
 *
 * The **review** — venue, table, the lines, the estimate, the pay-later notice —
 * and the **sent confirmation**, which is a different shape entirely: no top bar,
 * centred, and no way back into the cart it just committed. They are two returns
 * from one component rather than two routes because the transition is a state
 * change on the server, not a navigation the guest performs; `submitStatus`
 * swaps them and history is untouched.
 *
 * ## Retrying is safe, and the screen promises it
 *
 * `OrderProvider.submit()` reuses one `order_id` and one `Idempotency-Key` for
 * every attempt, so a second press cannot make a second order. That is why the
 * send button is **never disabled after a failure** — a dead button at that
 * exact moment is what makes a guest give up. On a network failure the screen
 * goes further and re-sends by itself when the browser comes back online, which
 * is the only thing that makes `q7.queued` ("กำลังส่งให้อัตโนมัติ") a true
 * sentence rather than a comforting one.
 *
 * ## No photos, by construction
 *
 * There is no image on this screen and no branch that would render one. The
 * no-photo case is not a fallback here — it is the only case, which is the right
 * default when most V1 merchants have no photography, and it is also the fastest
 * thing to paint on a cheap phone over 4G.
 *
 * ## An item that vanishes mid-session is loud, never silent
 *
 * `useCart().issues` is re-derived from the live projection on every render, so
 * an 86 that arrives on the stream while the guest is reading this screen marks
 * the line immediately, blocks the send, and offers a way out. The server can
 * also refuse at submit time with `item_unavailable`; those item ids are held in
 * `serverSoldOut` and marked the same way, because a guest whose dish disappears
 * quietly ends up eating something they did not choose.
 *
 * ## Money
 *
 * Nothing here is ever Pandan on this screen's own authority. Before submit
 * there is only a menu-price **estimate** (`q5.estimatedTotal` +
 * `q5.estimateNote`, tone `neutral`) — the server prices the bill and
 * `Order.totals` is the only real total. After submit the total is shown through
 * `orderTotalTone()`, which returns `pending` for an unpaid order and can only
 * return `verified` for money the server settled. The sent panel therefore
 * renders ink and Palm Sugar: a ticket reaching a kitchen is not money moving,
 * and the design's own success screen makes the same point with a Butterfly Pea
 * tick rather than a green one.
 *
 * ## No payment, no deposit, no card hold
 *
 * Walk-out risk is an open product question (03 §Q7) and nothing here calls a
 * payment endpoint. A hold, if one is ever decided, drops in between the summary
 * and the send bar without moving anything else.
 *
 * The reason used to be "the checkout endpoints are PLACEHOLDER (TBD)". They are
 * not, since 2026-08-11 — Q8 exists at `/pay/:orderId` and is reachable from the
 * bill (Q17). It is deliberately **not** reachable from this screen's sent panel:
 * `q7.sentBody` says payment happens later at the counter, which is the truth for
 * an order-first venue, and **nothing in the contract says whether a venue is
 * order-first or pay-first** (C-2 deferred, D-024). Offering to pay here would be
 * this surface guessing the venue's configuration on the screen where it has just
 * told the guest the opposite.
 *
 * ## Astryx, and where the look now comes from
 *
 * Nothing is imported from astryx any more except the `MoneyTone` *type*. Every
 * surface decision — the shell, the card, the pill, the five button weights, the
 * terminal notice, the money colour — comes from `src/ui`, which is where the
 * thirteen guest screens agree with each other. If guest checkout is stripped to
 * a token-only Astryx subset (`brand.html` §6) this screen does not notice:
 * `src/ui` is CSS custom properties and `global.css` all the way down.
 */

// --- little things -----------------------------------------------------------

function reasonKey(reason: UnavailabilityReason | null | undefined): MessageKey | null {
  if (!reason) return null;
  return `reason.${reason}` as MessageKey;
}

/** Icons are drawn, not fetched: two paths cost less than one request. */
function AlertMark() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
      <path
        d="M10 2.5 18.5 17.5H1.5L10 2.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M10 8v3.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="10" cy="14.6" r="1" fill="currentColor" />
    </svg>
  );
}

// --- style -------------------------------------------------------------------
//
// What is left of this section, and why it is short now.
//
// It used to hold the whole screen: a page shell, a top bar, a card, a pill, two
// buttons, a send bar. All seven of those are in `src/ui` now, drawn once for
// thirteen screens, so what remains here is only the geometry the shared
// primitives do not have an opinion about — a quantity column that has to line
// up with the one on the receipt, and two flex rows.
//
// Every value is still a theme custom property. There is no hex literal in this
// file and there must not be one: `src/theme/surfaceTokens.ts` is the single
// place a colour the theme lacks may be declared, and it is not this screen's.

/**
 * The quantity column. A fixed measure rather than `auto`, so a `1×` and a `12×`
 * put the dish names on the same left edge — a ragged column of names is read as
 * a rendering fault before it is read as an order.
 */
const quantityCell: CSSProperties = {
  flex: '0 0 auto',
  minWidth: '3ch',
};

/** Quantity, name column, price. The receipt row, on the page ground. */
const lineRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--pokpok-space-4)',
};

/** Mark plus text, in a notice. The mark keeps its own baseline, never the text's. */
const noticeRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 'var(--pokpok-space-2)',
};

const bareList: CSSProperties = {listStyle: 'none', margin: 0, padding: 0};

/** Wraps for an inline action that follows a sentence of explanation. */
const inlineWrap: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 'var(--pokpok-space-2)',
};

/**
 * Something the guest has to read before pressing send.
 *
 * `warning` is Palm Sugar and takes the shared warning card: attention is needed
 * and nothing has gone wrong — a dish went 86 while they were reading.
 *
 * `error` is a **white card with a Dried Chili mark**, not a red panel, and that
 * is the design package's own answer rather than a compromise. The one screen it
 * draws for a failure the guest did not cause — the unverified payment result —
 * is a plain white card with an ink headline, because the copy blames nobody and
 * a red wash would. `q7.connectionLostTitle` and `q7.rejectedTitle` are exactly
 * that kind of news: the send did not land, pressing again is safe, and a guest
 * frightened by the colour is a guest who waves at a server instead.
 *
 * The colour is never the meaning either way — the title says it in words, and
 * the mark only reinforces it.
 *
 * ESCALATION (see the report): `Card` has no `error` tone, so Dried Chili is
 * carried on the glyph here. If a red-tinted card is ever wanted, it belongs in
 * `src/ui/Card.tsx` beside the other three, not in a screen.
 */
function Notice({
  kind,
  title,
  children,
}: {
  kind: 'warning' | 'error';
  title: string;
  children?: ReactNode;
}) {
  return (
    <Card tone={kind === 'warning' ? 'warning' : 'default'}>
      <div style={noticeRow}>
        <span
          style={{
            color: kind === 'error' ? 'var(--color-error)' : 'var(--color-warning)',
            flex: '0 0 auto',
            lineHeight: 0,
            paddingTop: 4,
          }}
        >
          <AlertMark />
        </span>
        <div className="pk-stack">
          {/* Colour never carries the meaning on its own — the label always says it too. */}
          <strong>{title}</strong>
          {/* A stack rather than one run of text: a notice that carries both what
              happened and what to do about it must keep them as two sentences a
              guest can find separately, not one paragraph they have to parse. */}
          {children ? <div className="pk-muted pk-stack">{children}</div> : null}
        </div>
      </div>
    </Card>
  );
}

// --- screen ------------------------------------------------------------------

export default function Q07OrderSubmit() {
  const {t, ln} = useI18n();
  const {navigate} = useRouter();
  const {session, status: sessionStatus} = useGuestSession();
  const {availabilityOf} = useMenu();
  const cart = useCart();
  const {submit, submitStatus, order, error, reset} = useOrder();

  /**
   * Item ids the **server** refused, from `item_unavailable.details.item_ids`.
   * The projection may not have caught up with the KDS yet, so the refusal is
   * held here and rendered exactly like a live 86 rather than being reduced to
   * one line of error text with no clue which dish it means.
   */
  const [serverSoldOut, setServerSoldOut] = useState<readonly string[]>([]);

  const [online, setOnline] = useState(() => globalThis.navigator?.onLine ?? true);

  useEffect(() => {
    const update = () => setOnline(globalThis.navigator?.onLine ?? true);
    globalThis.addEventListener('online', update);
    globalThis.addEventListener('offline', update);
    return () => {
      globalThis.removeEventListener('online', update);
      globalThis.removeEventListener('offline', update);
    };
  }, []);

  useEffect(() => {
    if (error?.code === 'item_unavailable') setServerSoldOut(error.unavailableItemIds);
  }, [error]);

  const send = useCallback(() => {
    setServerSoldOut([]);
    void submit();
  }, [submit]);

  /**
   * A network failure re-sends itself when the connection returns.
   *
   * Only on the `online` event — no timer, no backoff loop, because a cheap
   * phone in a basement should not spend its battery discovering it is still in
   * a basement. The retry is the *same* submission (same `order_id`, same
   * `Idempotency-Key`), so if the first attempt did in fact arrive, this
   * resolves to that order rather than a second one.
   */
  const failedOffline = submitStatus === 'failed' && error?.kind === 'network';
  useEffect(() => {
    if (!failedOffline) return;
    const retry = () => void submit();
    globalThis.addEventListener('online', retry);
    return () => globalThis.removeEventListener('online', retry);
  }, [failedOffline, submit]);

  // Every line that must not be sent, from either source, keyed by line id.
  const soldOutLines = useMemo(() => {
    const byLine = new Map<string, UnavailabilityReason | null>();
    for (const issue of cart.issues) {
      if (issue.kind === 'reprice') continue;
      byLine.set(issue.line.line_id, availabilityOf(issue.line.item_id)?.reason ?? null);
    }
    for (const line of cart.lines) {
      if (serverSoldOut.includes(line.item_id) && !byLine.has(line.line_id)) {
        byLine.set(line.line_id, availabilityOf(line.item_id)?.reason ?? null);
      }
    }
    return byLine;
  }, [cart.issues, cart.lines, serverSoldOut, availabilityOf]);

  const repricedLines = useMemo(() => {
    const byLine = new Map<string, number>();
    for (const issue of cart.issues) {
      if (issue.kind === 'reprice' && issue.currentUnitPriceSatang !== undefined) {
        byLine.set(issue.line.line_id, issue.currentUnitPriceSatang);
      }
    }
    return byLine;
  }, [cart.issues]);

  const blocked = soldOutLines.size > 0;
  const submitting = submitStatus === 'submitting';
  // The merchant's own name for this table, straight off the session (C-1,
  // approved 2026-08-09). Null for a queue-ahead session, which is bound to no
  // table, and the line is simply not drawn.
  //
  // **A UUID is never printed here.** What stood here before C-1 derived a label
  // from `table_id` when it "looked readable" — short and not UUID-shaped. No
  // such value was ever sent, so the branch was dead; the day the id shape
  // changed it would have shown a guest a database identifier with nobody
  // having chosen that.
  const tableLabel = session?.table_label ?? null;
  const venue = ln(session?.merchant_display_name);

  // --- terminal and near-terminal states ------------------------------------

  // Sent. The order is the server's now and the cart has been cleared.
  if (submitStatus === 'sent' && order) {
    return (
      <Sent
        heading={t('q7.sentTitle')}
        bodyText={t('q7.sentBody')}
        orderNumber={order.order_number ?? null}
        totalSatang={order.totals.total_satang}
        tone={orderTotalTone(order)}
        totalLabel={t('common.total')}
        statusLabel={t('q7.viewStatus')}
        againLabel={t('q7.orderAgain')}
        onAgain={() => {
          reset();
          navigate(ROUTES.menu);
        }}
      />
    );
  }

  // The cart is empty. Either this phone's round is already with the kitchen, or
  // there is nothing to review at all — two different sentences.
  if (cart.lines.length === 0) {
    if (order) {
      return (
        <Sent
          heading={t('q7.alreadySent')}
          bodyText={t('q7.sentBody')}
          orderNumber={order.order_number ?? null}
          totalSatang={order.totals.total_satang}
          tone={orderTotalTone(order)}
          totalLabel={t('common.total')}
          statusLabel={t('q7.viewStatus')}
          againLabel={t('q7.orderAgain')}
          onAgain={() => {
            reset();
            navigate(ROUTES.menu);
          }}
        />
      );
    }
    return (
      <Screen>
        {/* INTEGRATION · reachable from every state, not only the review one. */}
        <TopBar trailing={<LanguageToggle variant="link" />} />
        <ScreenBody center>
          <h1 className="pk-h1">{t('q7.title')}</h1>
          <p className="pk-muted">{t('q5.empty')}</p>
        </ScreenBody>
        <ScreenFooter bordered={false}>
          {/*
            An ink outline, not the solid red, and the design makes this exact
            argument on the menu: its bottom bar drops out of Chili Red the
            moment the cart is empty, because with nothing to send there is no
            forward action to spend the screen's one red on. Going to look at
            the menu is a way out, not a commitment.
          */}
          <ActionLink to={ROUTES.menu} variant="secondary" block>
            {t('common.viewMenu')}
          </ActionLink>
        </ScreenFooter>
      </Screen>
    );
  }

  // No usable credential: the table QR is the login, and submitting without one
  // cannot work. Q19 owns the re-scan; this only has to point at it honestly.
  //
  // Drawn as a terminal notice rather than as a banner over a summary the guest
  // can no longer act on: there is exactly one thing to do from here, and a
  // screen that still shows a send button beside "your QR expired" is offering a
  // press that cannot work.
  if (sessionStatus === 'idle' || sessionStatus === 'expired' || sessionStatus === 'invalid') {
    return (
      <Screen>
        <ScreenBody center>
          <TerminalNotice tone="error" glyph="!" title={t('error.session')}>
            <p className="pk-muted">{t('common.askStaff')}</p>
          </TerminalNotice>
        </ScreenBody>
        <ScreenFooter bordered={false}>
          {/*
            Secondary, not primary. Re-scanning is the resolution, but the button
            only navigates to Q19 — another notice screen — and the actual work is
            done with a camera. A solid red here promises a fix the tap does not
            deliver.
          */}
          <ActionLink to={ROUTES.invalid} variant="secondary" block>
            {t('q19.rescan')}
          </ActionLink>
          {/* INTEGRATION · no top bar on a centred screen, so the switcher lives here. */}
          <div style={{display: 'flex', justifyContent: 'center'}}>
            <LanguageToggle variant="link" />
          </div>
        </ScreenFooter>
      </Screen>
    );
  }

  /*
   * --- the review that is no longer reached --------------------------------
   *
   * Owner directive, 2026-08-14: the confirm step is the sheet on Q5, which is
   * how the approved package draws it. Nothing routes here with an unsent cart
   * any more, so a guest arriving in that state got here by typing the URL, by
   * a bookmark, or by a back gesture out of a submission — and the honest answer
   * to all three is the cart, which is now the only place an order is reviewed
   * and the only place it can be sent from.
   *
   * `replace`, so the back gesture does not bounce between the two.
   *
   * The panels above this line are what this screen is for now: sent, rejected,
   * queued, and the connection-lost retry. They are the package's `success`
   * screen plus the three failures it does not draw.
   */
  if (cart.lines.length > 0 && submitStatus === 'idle') {
    navigate(ROUTES.cart, {replace: true});
    return null;
  }

  return (
    <Screen>
      <TopBar
        leading={<BackLink to={ROUTES.cart} label={t('common.back')} />}
        // INTEGRATION · the one shared control (Q02).
        trailing={<LanguageToggle variant="link" />}
      />

      {/*
        `scroll` because this screen has a pinned footer, and the footer is
        pinned by flex rather than `position: fixed` — a fixed bar loses to the
        collapsing URL bar and to the on-screen keyboard, both of which are
        unavoidable on a guest's own phone.
      */}
      <ScreenBody scroll>
        <Stack gap="wide">
          <header className="pk-stack">
            {venue ? <p className="pk-muted">{venue}</p> : null}
            <h1 className="pk-h1">{t('q7.title')}</h1>
            {tableLabel ? (
              <p className="pk-muted">{t('common.table', {label: tableLabel})}</p>
            ) : null}
          </header>

          {/*
            The debt disclosure, on the accent-muted card the design draws for it
            — a calm tinted note, not a warning, because nothing has gone wrong.
            It sits above the summary rather than beside the send button, where a
            guest who scrolls straight to send would pass it.
          */}
          <Card tone="accent">{t('q7.payLaterNote')}</Card>

          {/*
            The 86 announcement. It names no dish here on purpose — the lines
            themselves are struck through and labelled a few centimetres below,
            and repeating the dish name in a banner is how a guest ends up
            reading the same bad news twice and still scrolling to find it.
          */}
          {blocked ? <Notice kind="warning" title={t('q7.someItemsSoldOut')} /> : null}

          <section aria-labelledby="q7-summary">
            {/*
              A plain header row, not `SectionHeader`: that primitive draws a
              hairline under itself, and the first line below already carries a
              hairline above it. Two rules a pixel apart read as a mistake.
            */}
            <SplitRow>
              <h2 id="q7-summary" className="pk-section-title">
                {t('q7.summary')}
              </h2>
              <span className="pk-muted">{t('common.items', {count: cart.count})}</span>
            </SplitRow>

            {/*
              Receipt rows on the page ground, separated by the warm hairline —
              the design's own treatment for a list of ordered things, and the
              same one Q14 and Q17 use, so the guest reads one order three times
              in one shape.
            */}
            <ul style={bareList}>
              {cart.lines.map((line) => (
                <Line
                  key={line.line_id}
                  line={line}
                  soldOut={soldOutLines.has(line.line_id)}
                  reason={reasonKey(soldOutLines.get(line.line_id) ?? null)}
                  newPriceSatang={repricedLines.get(line.line_id) ?? null}
                  onRemove={() => cart.remove(line.line_id)}
                  onAcceptPrice={(price) => cart.acceptNewPrice(line.line_id, price)}
                />
              ))}
            </ul>
          </section>
        </Stack>
      </ScreenBody>

      <ScreenFooter>
        {/*
          Live region: submitting, queued and every failure are announced without
          moving focus away from the button the guest is about to press again.
        */}
        <div role="status" aria-live="polite" className="pk-stack">
          {!online && !failedOffline ? <span className="pk-muted">{t('common.offline')}</span> : null}

          {failedOffline ? (
            online ? (
              <Notice kind="error" title={t('q7.connectionLostTitle')}>
                {t('q7.connectionLostAction')}
              </Notice>
            ) : (
              <Notice kind="warning" title={t('q7.connectionLostTitle')}>
                {t('q7.queued')}
              </Notice>
            )
          ) : null}

          {submitStatus === 'failed' && error && error.kind !== 'network' ? (
            error.code === 'item_unavailable' ? (
              // Only when the refused ids matched nothing in the cart — otherwise
              // the lines are already marked and the banner above already said it.
              blocked ? null : (
                <Notice kind="warning" title={t('q7.someItemsSoldOut')}>
                  {t('q7.backToCart')}
                </Notice>
              )
            ) : (
              <Notice kind="error" title={t('q7.rejectedTitle')}>
                <span>{t(errorMessageKey(error))}</span>
                <span>{t('q7.rejectedAction')}</span>
              </Notice>
            )
          ) : null}
        </div>

        {/*
          The number the guest is committing to, immediately above the button
          that commits them — the design draws it in the send bar, not at the
          bottom of the list, and it is right: a total the thumb has scrolled
          past is a total nobody read.

          An estimate, and it says so. The server prices the bill from the tax
          shape stored on the order; a client that presented this as a total
          would be quoting a number it is not entitled to quote. Neutral tone —
          money-shaped, no settlement meaning, and never green before a server
          has settled anything.
        */}
        <SplitRow>
          <span>{t('q5.estimatedTotal')}</span>
          <Money satang={cart.estimatedSubtotalSatang} tone="neutral" size="title" />
        </SplitRow>
        {/*
          The design pulls this line up against the figure with `margin-top: -6`.
          Reproduced as the footer's own 8px gap rather than a negative margin:
          the intent is "these two belong together", and a negative margin that
          collides once the text wraps to three lines on a scaled-up phone is not
          that intent.
        */}
        <p className="pk-hint">{t('q5.estimateNote')}</p>

        {blocked ? (
          // Sending would be refused for the same reason it is already blocked.
          // The way forward is the cart, so that is what the primary slot holds —
          // and it is still exactly one solid red on the screen.
          <ActionLink to={ROUTES.cart} variant="primary">
            {t('q7.backToCart')}
          </ActionLink>
        ) : (
          <Button
            variant="primary"
            onClick={send}
            // Disabled only while a request is genuinely in flight. **Never after
            // a failure** — that is the moment a guest gives up and waves.
            //
            // No `disabledReason` here, and that is deliberate rather than an
            // omission: `Button` asks for one because the design's disabled
            // primary explains itself nowhere, but this button's own label has
            // already changed to `q7.submitting` and `aria-busy` announces the
            // same fact. A hint line that appears and vanishes under the button
            // during a two-second send is noise, and it would push the button
            // itself down under a thumb already on its way.
            disabled={submitting}
            aria-busy={submitting}
          >
            {submitting ? t('q7.submitting') : t('q7.submit')}
          </Button>
        )}

        {/*
          A text button, the weight the design uses for the aside under a send
          bar. It duplicates the top bar's back link on purpose: a thumb resting
          on the send button is nowhere near the top of a phone, and this is the
          moment a guest most wants to change one thing before committing.
        */}
        {!blocked ? (
          <ActionLink to={ROUTES.cart} variant="text" block>
            {t('q7.backToCart')}
          </ActionLink>
        ) : null}
      </ScreenFooter>
    </Screen>
  );
}

// --- one line of the summary -------------------------------------------------

function Line({
  line,
  soldOut,
  reason,
  newPriceSatang,
  onRemove,
  onAcceptPrice,
}: {
  line: CartLine;
  soldOut: boolean;
  reason: MessageKey | null;
  newPriceSatang: number | null;
  onRemove: () => void;
  onAcceptPrice: (priceSatang: number) => void;
}) {
  const {t, ln, money} = useI18n();
  const name = ln(line.name_snapshot);

  return (
    <li className="pk-ruled" style={lineRow}>
      <span className="pk-muted pokpok-money" style={quantityCell}>
        <span className="pokpok-sr-only">{t('common.quantity')} </span>
        {line.quantity}×
      </span>

      <div className="pk-stack" style={{flex: 1, minWidth: 0}}>
        <span
          className="pk-strong"
          style={{
            // Struck through as well as labelled: colour and one badge are not
            // enough on a cracked screen in direct sunlight.
            textDecoration: soldOut ? 'line-through' : undefined,
            color: soldOut ? 'var(--color-text-secondary)' : undefined,
          }}
        >
          {name}
        </span>

        {line.modifiers.length > 0 ? (
          <span className="pk-muted">
            {line.modifiers
              .map((modifier) =>
                modifier.quantity > 1
                  ? `${ln(modifier.name_snapshot)} ×${modifier.quantity}`
                  : ln(modifier.name_snapshot),
              )
              .join(' · ')}
          </span>
        ) : null}

        {line.note ? (
          <span className="pk-muted">
            {t('q4.noteToKitchen')}: {line.note}
          </span>
        ) : null}

        {soldOut ? (
          <span style={inlineWrap}>
            <StatusPill tone="warning">{t('common.soldOut')}</StatusPill>
            {reason ? <span className="pk-muted">{t(reason)}</span> : null}
            {/*
              Outlined Dried Chili with a word in the label, never a bare ✕ and
              never a fill — the other half of the red rule. Removing a line the
              kitchen cannot cook is destructive and the guest should see that it
              is, even though it is also the only way forward from here.
            */}
            <Button variant="destructive" onClick={onRemove}>
              {t('q5.lineSoldOutAction')}
            </Button>
          </span>
        ) : null}

        {/*
          A price that moved between the menu and this screen. Not a blocker —
          the server charges the real number either way — but a guest is about to
          commit, and committing to a number they were not shown is the thing
          this acknowledgement exists to prevent.
        */}
        {!soldOut && newPriceSatang !== null ? (
          <span style={inlineWrap}>
            <span className="pk-muted">
              {t('q5.linePriceChanged', {item: name, price: money(newPriceSatang)})}
            </span>
            <Button variant="text" onClick={() => onAcceptPrice(newPriceSatang)}>
              {t('q5.linePriceChangedAction')}
            </Button>
          </span>
        ) : null}
      </div>

      <Money satang={lineTotalSatang(line)} tone="neutral" />
    </li>
  );
}

// --- the sent panel ----------------------------------------------------------

/**
 * The confirmation, and the second of this screen's two moments.
 *
 * # No top bar, and that is the design's decision rather than an omission
 *
 * The order is committed and there is nowhere to go back to. Both ways out are
 * in the footer and both of them move forward — the status screen, or the menu
 * for another round. `onAgain` resets the order and navigates rather than
 * pushing, so the back gesture cannot land a guest on a cart they already sent.
 *
 * # The tick is Butterfly Pea and the total is ink
 *
 * `TerminalNotice tone="info"` is the blue ring the design draws here, and it is
 * the money rule doing exactly its job: food is ordered, no money has moved, so
 * the confirmation is not green. The total takes whatever `orderTotalTone()`
 * says — `pending` for an order-first ticket, which renders as plain ink,
 * because the design system has no tone meaning "probably paid" and will not be
 * given one. The unresolved half is said in a **word**, not by tinting the
 * figure: `q17.unpaid` on a Palm Sugar pill beside it.
 *
 * If the venue settles the bill at the counter while this page is still open,
 * the same call is what turns the figure and the card green, and only then.
 */
function Sent({
  heading,
  bodyText,
  orderNumber,
  totalSatang,
  tone,
  totalLabel,
  statusLabel,
  againLabel,
  onAgain,
}: {
  heading: string;
  bodyText: string;
  orderNumber: string | null;
  totalSatang: number;
  /** From `orderTotalTone(order)` at the call site. Never a literal. */
  tone: MoneyTone;
  totalLabel: string;
  statusLabel: string;
  againLabel: string;
  onAgain: () => void;
}) {
  const {t} = useI18n();
  // The money rule, already decided: this is `orderTotalTone(order)` arriving
  // from the caller, which is the only sanctioned source of `verified` on the
  // surface. The card follows the figure rather than making a second judgement.
  const paid = tone === 'verified';

  return (
    <Screen>
      {/*
        The language control belongs in the top bar, where it is on every other
        screen. It used to sit in this footer under both buttons, on the written
        reasoning that this screen "has no top bar to put it in" — which was true
        and was the wrong conclusion: the screen had no top bar because nothing
        had asked for one, and the consequence was the one control a guest who
        cannot read the page needs, placed below everything they cannot read, at
        the bottom of the screen. Foundations §8 wants it reachable, not merely
        present. Moved 2026-08-14.

        Nothing else goes in this bar. There is deliberately no back link: the
        order is with the kitchen and there is nothing behind this screen to
        return to.
      */}
      <TopBar trailing={<LanguageToggle variant="link" />} />

      <ScreenBody center>
        <TerminalNotice tone="info" glyph="✓" title={heading}>
          <p className="pk-muted" style={{maxWidth: '34ch'}}>
            {bodyText}
          </p>
          {orderNumber ? (
            <p className="pk-hint pokpok-money">{t('q17.orderNumber', {number: orderNumber})}</p>
          ) : null}
        </TerminalNotice>

        {/* `pk-body--center` centres and shrink-wraps its children; the money
            card is the one thing on the screen that wants the full measure, and
            it reads left-aligned like every other total on the surface. */}
        <div style={{width: '100%', textAlign: 'start'}}>
          <Card tone={paid ? 'verified' : 'default'}>
            {/* No "ยังไม่ได้ชำระ" pill. The order-sent screen says the food is
                with the kitchen; whether the bill is paid is not this moment's
                point, and the owner asked to drop it (2026-08-17). The figure is
                still ink-not-green until money is verified — the money rule holds
                without a word for it here. */}
            <SplitRow>
              <span className="pk-muted">{totalLabel}</span>
              <Money satang={totalSatang} tone={tone} size="title" />
            </SplitRow>
          </Card>
        </div>
      </ScreenBody>

      <ScreenFooter bordered={false}>
        <ActionLink to={ROUTES.status} variant="primary">
          {statusLabel}
        </ActionLink>
        <Button variant="secondary" block onClick={onAgain}>
          {againLabel}
        </Button>
        {/*
          The venue leads, pokpokPOS is present and subordinate (03 §Q1). One
          line of body copy at the end of the flow — not a lockup, and there is
          no endorsement lockup anywhere on this surface (D-014).
        */}
        <p className="pk-hint" style={{textAlign: 'center'}}>
          {t('common.attribution')}
        </p>
      </ScreenFooter>
    </Screen>
  );
}
